import React, { useContext, useState, useEffect, useMemo } from "react";
import { useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";
import { ClassroomBooking, ClassroomBookingStatusPayload, Session } from "@models/ISchema";
import RemoveBookingModal from "../RemoveBookingModal/RemoveBookingModal";
import { BookingContext } from "../../context/BookingContext";
import useGetIntervals from "../../hooks/useIntervals";
import { classroomBookingChangeStatusMutation } from "../../graphql/classroomBookingChangeStatus.mutation";
import * as utils from "../../utils";
import { Types } from "../../context/search.reducer";
import { Types as RequestTypes } from "../../context/request.reducer";
import { getChangeStatusFields } from "@modules/booking/pages/BookingList/bookingFilterVariables";
import Detail from "./Detail";
import Resources from "./Resources";
import State from "./State";
import WeekTooltipLabel from "@components/week-tooltip-label";
import { getIsCurrentWeek } from "@modules/booking/utils/classroomEvents";
import { Heading, RoundButton, Loading, SkeletonBase, Button, toast } from "@foris/avocado-suite";
import { Header } from "@common/components";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@common/hooks/useIsMobile";
import useBookingContextUrl from "@modules/booking/hooks/useBookingContextUrl";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import { getClassroomBookingValidationMessage } from "@modules/booking/utils/getClassroomBookingValidationMessage";
import css from "./cardDetail.module.scss";

interface CardDetailProps {
  booking: ClassroomBooking;
  campus: string;
  isLoadingDetails?: boolean;
}

interface ISession {
  day: string;
  time: string;
}

const CardDetail: React.FC<CardDetailProps> = (props: CardDetailProps) => {
  const { booking, campus, isLoadingDetails } = props;
  const history = useHistory();
  const { adaptedHomeUrl } = useNavigationUrl();
  const { scenario, origin, id, contextUrl, isAdminBooking, queryParams } = useBookingContextUrl();
  const { state, dispatch } = useContext(BookingContext);
  const [intervals, getIntervals] = useGetIntervals();
  const [activeModal, setActiveModal] = useState(false);
  const [statusConfirmation, setStatusConfirmation] = useState<string>(null);
  const [editingState, setEditingState] = useState<boolean>(false);
  const [editingDetails, setEditingDetails] = useState<boolean>(false);
  const [notificationText, setNotificationText] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [classroomBookingChangeStatus] = useMutation(classroomBookingChangeStatusMutation, {});
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();

  const sessions: ISession[] = [];
  const weeks: string[] = [];
  const days: any[] = [];
  const IS_RECURRENT = booking?.isRecurrent;
  const pathBookings = `/booking/list/${contextUrl}${
    isAdminBooking || queryParams.get("from") === "app" ? `?campus=${campus}` : ""
  }`;

  const handleChangeStatus = async (status: string, comment: any) => {
    setLoading(true);
    try {
      const response = await classroomBookingChangeStatus({
        variables: {
          originId: origin,
          scenarioId: scenario,
          input: getChangeStatusFields({ bookingIds: [id], status, comment }),
        },
      });
      const payload: ClassroomBookingStatusPayload =
        response?.data?.cube?.classroomBookingChangeStatus;

      if (!payload?.commited) {
        toast.error(
          getClassroomBookingValidationMessage(
            t,
            payload?.validationErrors,
            "booking.booking-details.notifications.status-change-error",
          ),
          {
            autoClose: 3000,
          },
        );

        return;
      }

      window.location.reload();
    } catch (error) {
      console.error(error);
      toast.error(t("booking.booking-details.notifications.status-change-error"), {
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const weeklyItems = useMemo(() => {
    if (isLoadingDetails) return [];

    const dataIntervals = state?.search?.intervals?.map(week => {
      const { isCurrentWeek } = getIsCurrentWeek(week);

      return {
        id: week.id,
        label: week.value,
        isDisabled: false,
        isSelected: week?.selected,
        tooltipLabel: (
          <WeekTooltipLabel
            weekLabel={`S${week.value}`}
            startingDate={dayjs(week.startingDate)?.format("DD.MM.YYYY")}
            endingDate={dayjs(week.endingDate)?.format("DD.MM.YYYY")}
          />
        ),
        onClick: () => null,
        className: isCurrentWeek ? "currentWeek" : "",
      };
    });
    return dataIntervals;
  }, [state?.search?.intervals, isLoadingDetails]);

  const uniqueValues = (session: Session, type: "week" | "day") => {
    const sessionBlocks = session?.events.map(event => event.block);
    switch (type) {
      case "day":
        const uniqueDay = [...Array.from(new Set(sessionBlocks.map(item => item.day)))];
        days.push({
          day: uniqueDay[0],
          start: dayjs(session.scheduleEvent[0]?.start)?.format("HH:mm"),
          end: dayjs(session.scheduleEvent[0]?.end)?.format("HH:mm"),
        });
        break;
      case "week":
        const sessionIntervals = session?.events.map(event => event.interval);
        const unique = [...Array.from(new Set(sessionIntervals.map(item => item.id)))];
        weeks.push(unique[0]);
        break;
      default:
        return null;
    }
  };

  !isLoadingDetails &&
    booking?.sessions &&
    booking?.sessions.forEach(session => {
      uniqueValues(session, "day");
      uniqueValues(session, "week");
      session.scheduleEvent.forEach(event => {
        const day = utils.format.day(event.start);
        const startTime = utils.format.time(event.start);
        const endTime = utils.format.time(event.end);
        sessions.push({ day, time: `${startTime} - ${endTime}` });
      });
    });

  useEffect(() => {
    if (
      !state?.search?.intervals?.length &&
      !intervals?.data &&
      IS_RECURRENT &&
      !isLoadingDetails
    ) {
      getIntervals(scenario);
    }
  }, [isLoadingDetails, state?.search?.intervals]);

  useEffect(() => {
    if ((booking != null && Object.keys(booking).length === 0) || isLoadingDetails) return;
    dispatch({ type: Types.SetCurrentBooking, payload: booking });
    dispatch({ type: RequestTypes.SetEditedBooking, payload: booking });
  }, [booking, isLoadingDetails]);

  if (!booking?.sessions) return <div>No exite reserva</div>;

  if (!state?.search?.intervals?.length && IS_RECURRENT) {
    return intervals?.loading ? <Loading /> : null;
  }

  return (
    <section className={css.cardDetail}>
      {loading && <Loading />}

      <Header
        breadcrumbItems={[
          { title: t("home.breadcrumb.current"), path: adaptedHomeUrl() },
          { title: t("booking.title") },
        ]}
        title={t("booking.title")}
      />

      <RemoveBookingModal
        campus={campus}
        booking={booking}
        open={activeModal}
        onClose={() => {
          setActiveModal(false);
        }}
      />

      <div className={css.cardDetail_content}>
        <div className={css.contentHeader}>
          <div className={css.contentHeader_title}>
            <RoundButton
              size="sm"
              variant="ghost"
              icon="chevron-left"
              onClick={() => {
                if (history.length > 1 && queryParams.get("from") !== "app") {
                  history.goBack();
                } else {
                  history.replace(pathBookings);
                }
              }}
            />

            {isLoadingDetails ? (
              <SkeletonBase width="150px" height="24px" />
            ) : (
              <Heading type="h2">
                {t("booking.booking-details.title", {
                  name: booking?.title ? booking?.title : "",
                })}
              </Heading>
            )}
          </div>

          {isMobile ? (
            <RoundButton
              icon="trash"
              variant="secondary"
              size="md"
              disabled={isLoadingDetails}
              onClick={() => {
                setActiveModal(true);
              }}
            />
          ) : (
            <Button
              variant="secondary"
              leftIcon="trash"
              disabled={isLoadingDetails}
              onClick={() => {
                setActiveModal(true);
              }}
            >
              {t("booking.booking-details.actions.delete-booking")}
            </Button>
          )}
        </div>

        {/* Booking details */}
        <Detail booking={booking} editing={editingDetails} setEdit={setEditingDetails} />

        {/* Booking resources */}
        <Resources booking={booking} intervals={weeklyItems} />

        {/* Booking state */}
        <State
          booking={booking}
          editing={editingState}
          onClick={() => setEditingState(true)}
          onConfirmation={setStatusConfirmation}
          onNotification={setNotificationText}
          onCancel={() => setEditingState(false)}
          onSave={handleChangeStatus}
          statusConfirmation={statusConfirmation}
          notificationText={notificationText}
        />
      </div>
    </section>
  );
};

export default CardDetail;
